import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/uploadFile';
import { getProxiedImageUrl } from '@/services/imageProxyService';
import { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { ClothingItem, ClothingItemFormData, ShoeItem, ShoeItemFormData } from '@/models/types';

// Unified type for all db
export type ProductItem = ClothingItem | ShoeItem;
export type ProductItemFormData = ClothingItemFormData | ShoeItemFormData;

export async function fetchAffiliateConversion(rawLink: string): Promise<Record<string, string>> {
  if (!rawLink || typeof rawLink !== 'string') return {};
  const ensureProto = (v?: string) => {
    if (!v) return '';
    if (v.startsWith('http://') || v.startsWith('https://')) return v;
    return `https://${v}`;
  };
  const isWeidian = (v?: string) => {
    try {
      const u = new URL(ensureProto(v));
      const h = u.hostname.toLowerCase();
      return h === 'weidian.com' || h.endsWith('.weidian.com') || h === 'v.weidian.com' || h.endsWith('.v.weidian.com');
    } catch {
      return false;
    }
  };
  const normalize = (payload: any) => {
    const body = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
    return {
      rawlink: body?.rawlink || payload?.rawlink || '',
      id: body?.id || payload?.id || '',
      marketplace: body?.marketplace || payload?.marketplace || '',
      kakobuy: ensureProto(body?.kakobuy ?? payload?.kakobuy),
      oopbuy: ensureProto(body?.oopbuy ?? payload?.oopbuy),
      lovegobuy: ensureProto(body?.lovegobuy ?? payload?.lovegobuy),
      superbuy: ensureProto(body?.superbuy ?? payload?.superbuy),
      mulebuy: ensureProto(body?.mulebuy ?? payload?.mulebuy),
      joyagoo: ensureProto(body?.joyagoo ?? payload?.joyagoo),
      cnfans: ensureProto(body?.cnfans ?? payload?.cnfans),
      eastmallbuy: ensureProto(body?.eastmallbuy ?? payload?.eastmallbuy),
      orientdig: ensureProto(body?.orientdig ?? payload?.orientdig),
      loongbuy: ensureProto(body?.loongbuy ?? payload?.loongbuy),
      basetao: ensureProto(body?.basetao ?? payload?.basetao),
      itaobuy: ensureProto(body?.itaobuy ?? payload?.itaobuy),
      cssbuy: ensureProto(body?.cssbuy ?? payload?.cssbuy),
      sugargoo: ensureProto(body?.sugargoo ?? payload?.sugargoo),
      hoobuy: ensureProto(body?.hoobuy ?? payload?.hoobuy),
      ponybuy: ensureProto(body?.ponybuy ?? payload?.ponybuy),
      hipobuy: ensureProto(body?.hipobuy ?? payload?.hipobuy),
      litbuy: ensureProto(body?.litbuy ?? payload?.litbuy),
      gtbuy: ensureProto(body?.gtbuy ?? payload?.gtbuy),
      cnshopper: ensureProto(body?.cnshopper ?? payload?.cnshopper),
      mycnbox: ensureProto(body?.mycnbox ?? payload?.mycnbox),
      bbdbuy: ensureProto(body?.bbdbuy ?? payload?.bbdbuy),
      fansbuy: ensureProto(body?.fansbuy ?? payload?.fansbuy),
      pingubuy: ensureProto(body?.pingubuy ?? payload?.pingubuy),
      fishgoo: ensureProto(body?.fishgoo ?? payload?.fishgoo),
    };
  };

  try {
    const { data, error } = await supabase.functions.invoke('affiliate-convert', {
      body: { link: rawLink },
    });
    if (!error && data && (data as any).success !== false) {
      const out = normalize(data);
      if (out.rawlink || out.kakobuy || out.superbuy) return out;
    }
    if (error) {
      console.warn('affiliate-convert edge function error:', error);
    }
  } catch (e) {
    console.warn('affiliate-convert edge function failed:', e);
  }

  try {
    const url = `https://affiliate.aliofficials.com/convert?link=${encodeURIComponent(rawLink)}`;
    const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (!res.ok) {
      console.warn('affiliate direct convert failed:', res.status);
      return isWeidian(rawLink) ? normalize({ rawlink: ensureProto(rawLink) }) : {};
    }
    const json = await res.json();
    return normalize(json);
  } catch (e) {
    console.warn('affiliate direct convert failed:', e);
    return isWeidian(rawLink) ? normalize({ rawlink: ensureProto(rawLink) }) : {};
  }
}

export interface FilterOptions {
  category?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  searchTerm?: string;
  itemId?: string | number;
  sortOption?: string;
}

export const getdb = async (page = 1, perPage = 10, filters: FilterOptions = {}): Promise<{ data: ProductItem[]; count: number }> => {
  const applyCommonFilters = (q: any) => {
    let query = q;
    if (filters.category && filters.category !== '_all') query = query.eq('category', filters.category);
    if (filters.brand && filters.brand !== '_all') query = query.eq('brand', filters.brand);
    if (filters.minPrice !== undefined && !isNaN(filters.minPrice)) query = query.gte('price', filters.minPrice);
    if (filters.maxPrice !== undefined && !isNaN(filters.maxPrice)) query = query.lte('price', filters.maxPrice);
    if (filters.itemId !== undefined) {
      if (typeof filters.itemId === 'number' && !isNaN(filters.itemId)) query = query.eq('id', String(filters.itemId));
      else if (typeof filters.itemId === 'string' && filters.itemId.trim() !== '') query = query.eq('id', filters.itemId);
    }
    if (filters.searchTerm) {
      const term = String(filters.searchTerm).trim();
      // Exact id match when the term looks like a UUID (case-insensitive).
      // Partial ids must not use this path — invalid uuid.eq values error in PostgREST.
      const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(term);
      const textFilters = `name.ilike.%${term}%,brand.ilike.%${term}%,category.ilike.%${term}%,search_aliases.ilike.%${term}%,batch.ilike.%${term}%`;
      query = query.or(looksLikeUuid ? `id.eq.${term},${textFilters}` : textFilters);
    }
    return query;
  };

  let query = applyCommonFilters(
    supabase.from('db').select('*', { count: 'exact' })
  );

  // Apply sorting
  if (filters.sortOption) {
    const [field, direction] = filters.sortOption.split('_');
    if (field === 'id') {
      query = query.order('id', { ascending: direction === 'asc' });
    } else if (field === 'order') {
      query = query.order('order', { ascending: direction === 'asc', nullsFirst: false });
    } else {
      query = query.order(field, { ascending: direction === 'asc' });
    }
  } else {
    // Default sort by explicit order first, then by created_at desc
    query = query
      .order('order', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });
  }

  // Apply pagination
  const start = (page - 1) * perPage;
  query = query.range(start, start + perPage - 1);

  let { data, error, count } = await query;
  if (error) {
    // Fallback for schema/ordering incompatibilities: retry with simple created_at ordering.
    let fallback = applyCommonFilters(
      supabase
        .from('db')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
    );
    fallback = fallback.range(start, start + perPage - 1);
    const retry = await fallback;
    data = retry.data;
    count = retry.count;
    error = retry.error;
  }
  if (error) throw error;
  return { 
    // Normalize to always include _id for UI compatibility
    data: ((data || []) as any[]).map((item) => ({
      ...item,
      _id: item.id
    })) as unknown as ProductItem[], 
    count: count || 0 
  };
};

export const getProduct = async (id: string | number): Promise<ProductItem> => {
  const lookupId = typeof id === 'string' ? id : String(id);
  const { data, error } = await supabase
    .from('db')
    .select('*')
    .eq('id', lookupId)
    .limit(1);

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(`Item not found for id: ${lookupId}`);
  }
  const row = (data as any[])[0];
  const normalized = { ...row, _id: row.id };
  return normalized as unknown as ProductItem;
};

export const createProduct = async (formData: ProductItemFormData): Promise<ProductItem> => {
  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Proxy the image URL if it's a string
    imageUrl = await getProxiedImageUrl(formData.image);
  }

  const { data: { user } } = await supabase.auth.getUser();
  const timestamp = new Date().toISOString();

  // Determine next order position if not provided
  let nextOrder = formData && (formData as any).order;
  if (typeof nextOrder !== 'number' || isNaN(nextOrder)) {
    const { data: maxOrderData, error: maxOrderError } = await supabase
      .from('db')
      .select('order')
      .order('order', { ascending: false, nullsFirst: false })
      .limit(1);
    if (maxOrderError) throw maxOrderError;
    const currentMax = (maxOrderData && maxOrderData[0] && (maxOrderData[0] as any).order) || 0;
    nextOrder = (typeof currentMax === 'number' ? currentMax : 0) + 1;
  } else {
    // If a desired order is provided, free the slot by shifting others up
    await supabase.rpc('shift_orders_from', { p_order: nextOrder });
  }

  // Fetch affiliate links from the provided product link, if any
  let affiliateLinks: Record<string, string> = {};
  if ((formData as any).link) {
    affiliateLinks = await fetchAffiliateConversion((formData as any).link as string);
  }

  const insertData = {
    name: formData.name,
    brand: formData.brand,
    price: formData.price,
    image: imageUrl,
    // Save main link as raw/original supplier link if available, else original input
    link: affiliateLinks.rawlink || (formData as any).link,
    category: formData.category,
    batch: formData.batch,
    search_aliases: (() => {
      const raw = String((formData as any).search_aliases || '').trim();
      if (!raw) return null;
      return raw.length > 200 ? raw.slice(0, 200) : raw;
    })(),
    order: nextOrder,
    created_by: user?.email || 'Unknown user',
    created_at: timestamp,
    updated_at: timestamp,
    // Save affiliate columns if present (DB is expected to have these columns)
    kakobuy: affiliateLinks.kakobuy || null,
    oopbuy: affiliateLinks.oopbuy || null,
    lovegobuy: affiliateLinks.lovegobuy || null,
    superbuy: affiliateLinks.superbuy || null,
    mulebuy: affiliateLinks.mulebuy || null,
    joyagoo: affiliateLinks.joyagoo || null,
    cnfans: affiliateLinks.cnfans || null,
    eastmallbuy: affiliateLinks.eastmallbuy || null,
    orientdig: affiliateLinks.orientdig || null,
    loongbuy: affiliateLinks.loongbuy || null,
    basetao: affiliateLinks.basetao || null,
    itaobuy: affiliateLinks.itaobuy || null,
    cssbuy: affiliateLinks.cssbuy || null,
    sugargoo: affiliateLinks.sugargoo || null,
    hoobuy: affiliateLinks.hoobuy || null,
    ponybuy: affiliateLinks.ponybuy || null,
    hipobuy: affiliateLinks.hipobuy || null,
    litbuy: affiliateLinks.litbuy || null,
    gtbuy: affiliateLinks.gtbuy || null,
    cnshopper: affiliateLinks.cnshopper || null,
    mycnbox: affiliateLinks.mycnbox || null,
    bbdbuy: affiliateLinks.bbdbuy || null,
    fansbuy: affiliateLinks.fansbuy || null,
    pingubuy: affiliateLinks.pingubuy || null,
    fishgoo: affiliateLinks.fishgoo || null,
  } as any;

  const { data, error } = await supabase
    .from('db')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  const normalized = { ...(data as any), _id: (data as any).id };
  return normalized as unknown as ProductItem;
};

export const updateProduct = async (id: string | number, formData: ProductItemFormData): Promise<ProductItem> => {
  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Only proxy external images that aren't already from supabase
    if (!formData.image.includes('supabase.co')) {
      imageUrl = await getProxiedImageUrl(formData.image);
    } else {
      imageUrl = formData.image;
    }
  }

  const timestamp = new Date().toISOString();
  const requestedOrder = (formData as any).order as number | undefined;

  // Fetch affiliate links from the provided product link, if any
  let affiliateLinks: Record<string, string> = {};
  if ((formData as any).link) {
    affiliateLinks = await fetchAffiliateConversion((formData as any).link as string);
  }

  const updateData = {
    name: formData.name,
    brand: formData.brand,
    price: formData.price,
    image: imageUrl,
    // Save main link as raw/original supplier link if available, else provided link
    link: affiliateLinks.rawlink || (formData as any).link,
    category: formData.category,
    batch: formData.batch,
    search_aliases: (() => {
      const raw = String((formData as any).search_aliases || '').trim();
      if (!raw) return null;
      return raw.length > 200 ? raw.slice(0, 200) : raw;
    })(),
    updated_at: timestamp,
    // Save affiliate columns if present
    kakobuy: affiliateLinks.kakobuy || null,
    oopbuy: affiliateLinks.oopbuy || null,
    lovegobuy: affiliateLinks.lovegobuy || null,
    superbuy: affiliateLinks.superbuy || null,
    mulebuy: affiliateLinks.mulebuy || null,
    joyagoo: affiliateLinks.joyagoo || null,
    cnfans: affiliateLinks.cnfans || null,
    eastmallbuy: affiliateLinks.eastmallbuy || null,
    orientdig: affiliateLinks.orientdig || null,
    loongbuy: affiliateLinks.loongbuy || null,
    basetao: affiliateLinks.basetao || null,
    itaobuy: affiliateLinks.itaobuy || null,
    cssbuy: affiliateLinks.cssbuy || null,
    sugargoo: affiliateLinks.sugargoo || null,
    hoobuy: affiliateLinks.hoobuy || null,
    ponybuy: affiliateLinks.ponybuy || null,
    hipobuy: affiliateLinks.hipobuy || null,
    litbuy: affiliateLinks.litbuy || null,
    gtbuy: affiliateLinks.gtbuy || null,
    cnshopper: affiliateLinks.cnshopper || null,
    mycnbox: affiliateLinks.mycnbox || null,
    bbdbuy: affiliateLinks.bbdbuy || null,
    fansbuy: affiliateLinks.fansbuy || null,
    pingubuy: affiliateLinks.pingubuy || null,
    fishgoo: affiliateLinks.fishgoo || null,
  } as any;

  const { data, error } = await supabase
    .from('db')
    .update(updateData)
    .eq('id', typeof id === 'string' ? id : String(id))
    .select()
    .single();

  if (error) throw error;
  // If order change was requested, apply via bulk RPC for compatibility.
  if (typeof requestedOrder === 'number') {
    const { error: orderError } = await supabase
      .rpc('update_orders_bulk', { p_items: [{ id: typeof id === 'string' ? id : String(id), order: requestedOrder }] as any });
    if (orderError) throw orderError;
  }
  // Fetch latest to return consistent data
  return await getProduct(typeof id === 'string' ? id : String(id));
};

export const deleteProduct = async (id: string | number): Promise<void> => {
  const { error } = await supabase
    .from('db')
    .delete()
    .eq('id', typeof id === 'string' ? id : String(id));

  if (error) throw error;
};

export const updateProductOrder = async (id: string | number, orderValue: number): Promise<void> => {
  const { error } = await supabase
    .rpc('update_orders_bulk', { p_items: [{ id: typeof id === 'string' ? id : String(id), order: orderValue }] as any });
  if (error) throw error;
};

export const bulkUpdateProductOrders = async (items: Array<{ id?: string; _id?: string; order: number }>): Promise<void> => {
  if (!items || items.length === 0) return;
  const payload = items
    .map((it) => {
      const identifier = (typeof it._id === 'string' ? it._id : it.id) as string | undefined;
      if (!identifier) return null;
      return { id: identifier, order: it.order };
    })
    .filter(Boolean);

  const { error } = await supabase
    .rpc('update_orders_bulk', { p_items: payload as any });
  if (error) throw error;
};

// Export functions from the old services for backward compatibility
export const getClothingItems = getdb;
export const getClothingItem = getProduct;
export const createClothingItem = createProduct;
export const updateClothingItem = updateProduct;
export const deleteClothingItem = deleteProduct;

export const getShoeItems = getdb;
export const getShoeItem = getProduct;
export const createShoeItem = createProduct;
export const updateShoeItem = updateProduct;
export const deleteShoeItem = deleteProduct;
